/**
 * CAMPAIGN DRAFT STORE
 * ====================
 * P0 FIX: Centralized draft management with:
 * - Proper localStorage namespacing (userId:projectId)
 * - TTL management (30 days max)
 * - Flush on navigation/beforeunload
 * - State-aware UI feedback
 */

// localStorage key with userId and projectId namespace
export const DRAFT_STORAGE_KEY = (userId, projectId) => 
  `campaign_draft:${userId || 'anonymous'}:${projectId}`;

// TTL: 30 days in milliseconds
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Save draft to localStorage with TTL
 */
export function saveLocalDraft(userId, projectId, draft) {
  try {
    const key = DRAFT_STORAGE_KEY(userId, projectId);
    const data = {
      ...draft,
      savedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + DRAFT_TTL_MS).toISOString()
    };
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    console.warn('Failed to save draft to localStorage:', e);
    return false;
  }
}

/**
 * Load draft from localStorage (with TTL check)
 */
export function loadLocalDraft(userId, projectId) {
  try {
    const key = DRAFT_STORAGE_KEY(userId, projectId);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    
    // Check TTL
    if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (e) {
    console.warn('Failed to load draft from localStorage:', e);
    return null;
  }
}

/**
 * Delete draft from localStorage
 */
export function deleteLocalDraft(userId, projectId) {
  try {
    const key = DRAFT_STORAGE_KEY(userId, projectId);
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Purge expired drafts (call periodically)
 */
export function purgeExpiredDrafts() {
  try {
    const prefix = 'campaign_draft:';
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Corrupted data, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    return keysToRemove.length;
  } catch (e) {
    return 0;
  }
}

/**
 * Draft save states for UI feedback
 */
export const DRAFT_STATE = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  OFFLINE: 'offline',
  ERROR: 'error'
};

/**
 * Map legacy platform IDs to canonical ones
 * IMPORTANT: "email" -> "brevo"
 */
export function normalizePlatforms(platforms) {
  if (!platforms || !Array.isArray(platforms)) return [];
  
  return platforms.map(p => {
    if (p === 'email') return 'brevo';
    return p;
  });
}
